// JobHelp — the sheet a crew member opens when they do not recognise what they
// are looking at.
//
// The rules, and the three decisions behind them, are in ./job/jobHelp.js —
// beside subtaskMove.js, which is where this app's pure job rules live, and
// which also keeps `jobHelp.js` from colliding with `JobHelp.js` on a
// case-insensitive filesystem. CRA fails the build on that, correctly. In
// short: it is not AI and is not called AI, it is not a fourth Field tab, and
// the six safety notes work with no signal while every link does not.
//
// Reached from the Field JOB screen — somebody standing in a yard in front of
// the thing — and from Field Me, for when nobody is on a job yet.

import React, { useState } from 'react';
import { Sheet, FwButton, FwField, FwInput, Notice, SectionLabel } from '../../ui/fw';
import { Icon } from '../../ui';
import { helpState, QUICK } from './job/jobHelp';
import './JobHelp.css';

/**
 * @param {boolean} open
 * @param {function} onClose
 * @param {boolean} [online] whether links are worth offering — Field passes
 *   useOffline()'s `online`, so a phone with no bars gets the notes and a
 *   sentence rather than a button onto a connection error.
 */
export default function JobHelp({ open, onClose, online = true }) {
  const [question, setQuestion] = useState('');
  const s = helpState({ question, online });

  // A new tab, and `noopener` — an external page must not get a handle on the
  // app's window. rel is not enough on its own for window.open.
  const openLink = (url) => { const w = window.open(url, '_blank', 'noopener,noreferrer'); if (w) w.opener = null; };

  return (
    <Sheet open={open} title="Job help" onClose={onClose} className="fh-help">
      {/* Said on the screen, not only in a comment. Somebody who thinks there
          is a model behind this asks it a question it cannot answer. */}
      <p className="fh-help__what">
        Six safety and method notes, and a search that opens Google. There is no
        assistant reading your question — the notes below are matched on words.
      </p>

      <FwField label="What are you looking at?" htmlFor="fh-help-q">
        <FwInput
          id="fh-help-q"
          type="search"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. outlet sparking, mould on ceiling"
        />
      </FwField>

      {s.tips.length > 0 ? (
        <div className="fh-help__tips">
          {s.tips.map((t) => (
            // tone carries the difference between "here is how" and "you can
            // be hurt by this" — jobHelp.js sets it, this only renders it.
            <Notice key={t.id} tone={t.tone} title={t.title}>{t.text}</Notice>
          ))}
        </div>
      ) : null}

      {s.noTipNote ? <p className="fh-help__note">{s.noTipNote}</p> : null}

      {s.offlineNote ? (
        <Notice tone="warn" title="No signal">{s.offlineNote}</Notice>
      ) : null}

      {s.links ? (
        <div className="fh-help__links">
          <SectionLabel>Search the web</SectionLabel>
          <FwButton variant="primary" icon="search" full onClick={() => openLink(s.links.google)}>
            {`Google "${s.links.query}"`}
          </FwButton>
          <FwButton variant="ghost" full onClick={() => openLink(s.links.youtube)}>
            Watch it on YouTube
          </FwButton>
        </div>
      ) : null}

      {s.photo.length > 0 ? (
        <div className="fh-help__links">
          <SectionLabel>Search by photo</SectionLabel>
          {s.photo.map((p) => (
            <button key={p.url} type="button" className="fh-help__photo" onClick={() => openLink(p.url)}>
              <Icon name="camera" />
              <span>
                <strong>{p.label}</strong>
                <em>{p.why}</em>
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {!question ? (
        <div className="fh-help__quick">
          <SectionLabel>Common questions</SectionLabel>
          {/* Fills the box rather than searching, so somebody can edit it
              before it leaves the app — the classic screen does the same. */}
          {QUICK.map((q) => (
            <button key={q.q} type="button" className="fh-help__chip" onClick={() => setQuestion(q.q)}>
              {q.label}
            </button>
          ))}
        </div>
      ) : null}
    </Sheet>
  );
}
